import React, { useContext, useState, lazy, Suspense, MouseEvent } from 'react';
import { Icon, Link, TextLarge, TextRegular, UtilsBlock } from '@hz-design-system/web-ui';
import { getFormattedNumber } from '@ecg-marktplaats/aurora-js-formatters';
import { Trans } from '@ecg-marktplaats/js-react-i18n';

import { trackGAEvent } from '../../../../utils/gaTracking';
import { GA_PARAMETER, GA_TRACK_EVENTS_ACTIONS } from '../../../../utils/constants';
import EnvironmentContext from '../../../../contexts/EnvironmentContext';
import createCustomDimension from '../../../../utils/createCustomDimension';
import TextSkeleton from '../../../../components/Skeleton/TextSkeleton';

import { TListingSellerInfo } from 'types/TListing';
import { TSellerProfileClient } from 'types/TSellerProfile';

import Classes from './SellerInfoSmall.scss';

type TSellerReviewsProps = {
  seller: TListingSellerInfo;
  profile: TSellerProfileClient;
};

const SellerReviewDialog = lazy(() =>
  import(/* webpackChunkName: "SellerReviewsDialog" */ '../../../../components/ReviewsDialog').then((m) => ({
    default: m.ReviewsDialog,
  })),
);

const getClickTrackingProps = ({
  reviewSystem = '',
  rating,
}: {
  reviewSystem: string | undefined;
  rating: number | undefined;
}): Parameters<typeof trackGAEvent> => [
  GA_TRACK_EVENTS_ACTIONS.SELLER_REVIEW_CLICK,
  reviewSystem,
  { ...createCustomDimension({ dimension: GA_PARAMETER.REVIEW_AVERAGE_SCORE, payload: rating }) },
];

const SellerReviews = ({ seller, profile }: TSellerReviewsProps) => {
  const { links: { seller: sellerLinks = {} } = {} } = useContext(EnvironmentContext);
  const sellerName = profile.title ?? seller.name;
  const [isOpen, setIsOpen] = useState(false);

  const isProfileLoaded = Object.keys(profile).length !== 0;

  const firstReviewSystemData = profile.reviews?.[0] ?? {};
  const { numberOfReviews, reviewSystem, rating } = firstReviewSystemData;

  const minScore = 0;
  const maxScore = 5;
  const isRatingValid = !!rating && rating >= minScore && rating <= maxScore;
  const withReviewAttribution = reviewSystem === 'GOOGLE_REVIEWS';

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const trackingProps = getClickTrackingProps({ reviewSystem, rating });
    trackGAEvent(...trackingProps);

    setIsOpen(true);
  };
  const handleClose = () => {
    setIsOpen(false);
  };

  const handleDialogTabClick = (tabReviewSystem?: string) =>
    trackGAEvent(GA_TRACK_EVENTS_ACTIONS.SELLER_REVIEW_TAB_CLICK, tabReviewSystem);

  const handleDialogExternalReviewsUrlClick = () =>
    trackGAEvent(GA_TRACK_EVENTS_ACTIONS.SELLER_REVIEW_EXTERNAL_LINK_CLICK);

  return (
    <div>
      {!isProfileLoaded && <TextSkeleton withAnimation={true} />}
      {isProfileLoaded && !numberOfReviews && (
        <>
          <Icon className={Classes.reviewIcon} name="review" color="text-brand-primary" />
          <Trans i18nKey="sellerReviews.noReviews" className={Classes.noReviewsPlaceholder} tagName="span" />
        </>
      )}
      {Boolean(numberOfReviews) && (
        <>
          <Icon className={Classes.reviewIcon} name="saved" color="text-brand-primary" />
          <TextLarge strong>{isRatingValid && `${rating.toFixed(1)} · `}</TextLarge>
          <Link href={sellerLinks.reviews} onClick={handleClick}>
            <Trans
              tagName="span"
              i18nKey="sellerInfo.review"
              count={numberOfReviews}
              formattedCount={getFormattedNumber(numberOfReviews)}
            />
          </Link>
          {withReviewAttribution && (
            <UtilsBlock as="span" marginTop="xxxs" className={Classes.reviewAttribution}>
              <Icon name="info" color="text-secondary" className="u-marginRightXxxxs" />
              <TextRegular color="text-secondary">Google Maps</TextRegular>
            </UtilsBlock>
          )}
        </>
      )}
      {isOpen && (
        <Suspense fallback={null}>
          <SellerReviewDialog
            onClose={handleClose}
            reviews={profile.reviews ?? []}
            sellerName={sellerName}
            internalReviewsUrl={sellerLinks.reviews}
            disableMultiLocation
            onTabClick={handleDialogTabClick}
            onExternalReviewsUrlClick={handleDialogExternalReviewsUrlClick}
          />
        </Suspense>
      )}
    </div>
  );
};

export default SellerReviews;
